import { useState, useEffect } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { motion } from 'framer-motion';
import { dialogBackdropVariants, dialogVariants } from '~/components/ui/Dialog';
import { classNames } from '~/utils/classNames';
import { BREAKPOINTS, useIsBelow } from '~/lib/hooks/useMediaQuery';
import { ProfileTab } from './tabs/ProfileTab';
import { PlansTab } from './tabs/PlansTab';
import { CreditUsageTab } from './tabs/CreditUsageTab';
import { TopUpTab } from './tabs/TopUpTab';
import { MemoryTab } from './tabs/MemoryTab';

type TabId = 'profile' | 'plans' | 'credits' | 'topup' | 'memory' | 'preferences';

interface Tab {
  id: TabId;
  label: string;
  icon: string;
}

interface TabGroup {
  label: string;
  tabs: Tab[];
}

const TAB_GROUPS: TabGroup[] = [
  {
    label: 'Account',
    tabs: [
      { id: 'profile', label: 'Profile', icon: 'i-ph:user-circle' },
      { id: 'memory', label: 'Memory', icon: 'i-ph:brain' },
    ],
  },
  {
    label: 'Billing',
    tabs: [
      { id: 'plans', label: 'Plan', icon: 'i-ph:credit-card' },
      { id: 'credits', label: 'Credit usage', icon: 'i-ph:chart-line-up' },
      { id: 'topup', label: 'Top up', icon: 'i-ph:lightning' },
    ],
  },
];

// Flatten for lookup
const ALL_TABS: Tab[] = TAB_GROUPS.flatMap((g) => g.tabs);

// Phones get a full-screen sheet. Framer drives the desktop centering transform
// inline (x/y -50%), so the sheet needs its own variants rather than CSS overrides.
const sheetVariants = {
  closed: { x: 0, y: 24, opacity: 0, transition: { duration: 0.15 } },
  open: { x: 0, y: 0, opacity: 1, transition: { duration: 0.15 } },
};

interface AccountSettingsModalProps {
  open: boolean;
  onClose: () => void;
  initialTab?: TabId;
}

export function AccountSettingsModal({ open, onClose, initialTab = 'profile' }: AccountSettingsModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const isSheet = useIsBelow(BREAKPOINTS.md);

  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
    }
  }, [open, initialTab]);

  const currentTab = ALL_TABS.find((t) => t.id === activeTab);

  function renderTabContent() {
    switch (activeTab) {
      case 'profile':
        return <ProfileTab />;
      case 'plans':
        return <PlansTab />;
      case 'credits':
        return <CreditUsageTab />;
      case 'topup':
        return <TopUpTab />;
      case 'memory':
        return <MemoryTab />;

      default:
        return null;
    }
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay asChild>
          <motion.div
            className="fixed inset-0 z-[9999] bg-black/60 md:backdrop-blur-md"
            initial="closed"
            animate="open"
            exit="closed"
            variants={dialogBackdropVariants}
          />
        </RadixDialog.Overlay>
        <RadixDialog.Content asChild>
          <motion.div
            className={classNames(
              'ui-root fixed z-[9999] focus:outline-none',
              isSheet
                ? 'inset-0 h-[100dvh] w-full'
                : 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[960px] max-w-[95vw] h-[760px] max-h-[90dvh]',
            )}
            initial="closed"
            animate="open"
            exit="closed"
            variants={isSheet ? sheetVariants : dialogVariants}
          >
            <div
              className={classNames(
                'relative flex h-full flex-col overflow-hidden bg-surface',
                isSheet
                  ? 'pt-[env(safe-area-inset-top)]'
                  : 'rounded-2xl border border-subtle shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)]',
              )}
            >
              {/* Header */}
              <div className="flex h-12 flex-shrink-0 items-center border-b border-hairline pl-[18px] pr-[18px] touch:pr-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="i-ph:gear-six text-[13px] text-muted" />
                  <span className="ui-caption tracking-[0.06em]">Settings</span>
                  {currentTab && (
                    <>
                      <span className="i-ph:caret-right ml-1 text-[9px] text-faint" />
                      <span className="text-[12.5px] font-medium tracking-[-0.005em] text-primary">
                        {currentTab.label}
                      </span>
                    </>
                  )}
                </div>
                <div className="flex-1" />
                <RadixDialog.Close asChild>
                  <button
                    className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted transition-colors duration-100 hover:bg-white/[0.06] touch:h-10 touch:w-10"
                    aria-label="Close"
                    onClick={onClose}
                  >
                    <span className="i-ph:x text-[14px]" />
                  </button>
                </RadixDialog.Close>
              </div>

              {/* Body — below lg the nav is a horizontally scrolling tab strip above the content */}
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:grid lg:grid-cols-[220px_1fr]">
                <nav className="modern-scrollbar flex flex-shrink-0 gap-1 overflow-x-auto border-b border-hairline bg-page px-2 py-2 lg:flex-col lg:gap-0 lg:overflow-y-auto lg:overflow-x-hidden lg:border-b-0 lg:border-r lg:px-2.5 lg:py-3.5">
                  {TAB_GROUPS.map((group, groupIdx) => (
                    <div key={group.label} className={classNames('contents lg:block', groupIdx !== 0 && 'lg:mt-4')}>
                      <div className="ui-caption hidden px-2.5 pb-2 pt-1 lg:block">{group.label}</div>
                      <div className="contents lg:flex lg:flex-col lg:gap-px">
                        {group.tabs.map((tab) => {
                          const isActive = activeTab === tab.id;
                          return (
                            <button
                              key={tab.id}
                              onClick={() => setActiveTab(tab.id)}
                              className={classNames(
                                'group flex flex-shrink-0 cursor-pointer items-center gap-2.5 whitespace-nowrap rounded-md border-0 px-2.5 py-[7px] text-left text-[12.5px] tracking-[-0.005em] transition-colors duration-100 touch:min-h-10',
                                isActive
                                  ? 'bg-sunken font-medium text-primary'
                                  : 'bg-transparent text-secondary hover:bg-white/[0.035]',
                              )}
                            >
                              <span
                                className={classNames(
                                  tab.icon,
                                  'flex-shrink-0 text-[14px] transition-opacity duration-100',
                                  isActive ? 'text-accent opacity-100' : 'opacity-70',
                                )}
                              />
                              <span>{tab.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </nav>

                {/* Content area */}
                <div className="settings-scrollbar min-h-0 flex-1 overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+20px)] pt-5 sm:px-6 md:px-8 md:pb-7 md:pt-7">
                  {renderTabContent()}
                </div>
              </div>
            </div>
          </motion.div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
